import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Loader2, Users, Calendar, Gift, TrendingUp, ArrowRight, Cake, AlertTriangle,
  DollarSign, Sparkles, Star, Crown, MessageCircle, Scissors,
  CheckCircle2, Circle, Wallet, Target,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useTerm } from "@/contexts/branding";
import { sendWhatsApp } from "@/lib/whatsapp";
import AttentionCard from "@/components/AttentionCard";

const STATUS_LABEL = { scheduled: "Agendado", completed: "Concluído", no_show: "Faltou", cancelled: "Cancelado" };
const STATUS_TONE = {
  scheduled: "bg-blue-500/10 text-blue-700 border-blue-500/30",
  completed: "bg-emerald-500/10 text-emerald-700 border-emerald-500/30",
  no_show: "bg-amber-500/10 text-amber-700 border-amber-500/30",
  cancelled: "bg-red-500/10 text-red-700 border-red-500/30",
};

function Tile({ icon: Icon, label, value, sub, tone = "" }) {
  return (
    <Card className="p-5 rounded-tenant border-border" data-testid={`dash-tile-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
      <div className="flex items-start justify-between mb-3">
        <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">{label}</div>
        <Icon className={`w-4 h-4 ${tone || "text-tenant"}`} strokeWidth={1.5} />
      </div>
      <div className="font-display font-semibold tracking-tight text-3xl">{value}</div>
      {sub && <div className="text-xs text-muted-foreground mt-2">{sub}</div>}
    </Card>
  );
}

function Section({ title, subtitle, action, children, testid }) {
  return (
    <section className="space-y-3" data-testid={testid}>
      <div className="flex items-end justify-between gap-3">
        <div>
          <h2 className="font-display text-lg font-semibold">{title}</h2>
          {subtitle && <p className="text-xs text-muted-foreground mt-0.5">{subtitle}</p>}
        </div>
        {action}
      </div>
      {children}
    </section>
  );
}

export default function TenantDashboard() {
  const { slug } = useParams();
  const auth = useAuth();
  const term = useTerm(auth?.tenant);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/tenant/dashboard/beauty");
        setData(data);
      } finally { setLoading(false); }
    })();
  }, []);

  if (loading) {
    return (
      <div className="p-10"><Loader2 className="w-5 h-5 animate-spin inline" /></div>
    );
  }

  const d = data || {};
  const revenue = d.sales_month?.revenue || 0;
  const inactive_total = (d.recurrence_counts?.warning_30 || 0) + (d.recurrence_counts?.critical_60 || 0) + (d.recurrence_counts?.lost_90 || 0);

  const appts_today_n = (d.appointments_today || []).length;
  const at_risk_n = d.recoverable?.at_risk_count ?? inactive_total;
  const low_stock_n = d.inventory_attention?.low_stock_count || 0;
  const recoverable_value = d.recoverable?.total_value || 0;
  const urgent = d.recoverable?.urgent_clients || [];
  const firstName = auth?.user?.name?.split(" ")[0] || "";
  const isEstudio = (auth?.tenant?.vertical || "").toLowerCase() === "estudio";
  const greeting = (() => {
    const h = new Date().getHours();
    if (h < 12) return "Bom dia";
    if (h < 18) return "Boa tarde";
    return "Boa noite";
  })();

  return (
    <div className="p-6 md:p-10 max-w-7xl space-y-8">
      {/* CABEÇALHO — Briefing Diário */}
      <div>
        <div className="text-xs uppercase tracking-[0.25em] text-muted-foreground">Visão Geral</div>
        <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mt-1" data-testid="tenant-dashboard-welcome">
          {greeting}{firstName ? `, ${firstName}` : ""}!
        </h1>
        <p className="text-sm sm:text-base text-muted-foreground mt-2 max-w-3xl leading-relaxed" data-testid="tenant-dashboard-briefing">
          Você tem{" "}
          <b className="text-foreground">{appts_today_n} agendamento{appts_today_n !== 1 ? "s" : ""}</b> hoje
          {!isEstudio && (
            <>
              ,{" "}
              <b className="text-foreground">{at_risk_n} cliente{at_risk_n !== 1 ? "s" : ""}</b> em risco de abandono
            </>
          )}
          {" "}e{" "}
          <b className="text-foreground">{low_stock_n} ite{low_stock_n !== 1 ? "ns" : "m"} de estoque</b> precisando de atenção.
        </p>
      </div>

      {/* Onboarding Guiado — só nos primeiros 7 dias */}
      {d.onboarding?.show && <OnboardingChecklist slug={slug} onboarding={d.onboarding} />}

      {/* Central de Valor Recuperável + Ações Rápidas de WhatsApp
          Oculto em nicho Estúdio (atendimento pontual, sem lógica de churn). */}
      {!isEstudio && (
        <RecoverySection slug={slug} value={recoverable_value} atRiskCount={at_risk_n} urgent={urgent} />
      )}

      {/* Pedidos vindos do portal do cliente aguardando ação (item 4).
          - Pacotes: /t/{slug}/financeiro?tab=packages (bloco "Pedidos do portal")
          - Resgates: /t/{slug}/fidelidade?tab=redeem   (bloco "Pedidos do portal") */}
      {((d.pending_requests?.package || 0) > 0 || (d.pending_requests?.redemption || 0) > 0) && (
        <section
          data-testid="dashboard-portal-pending"
          className="grid gap-3 sm:grid-cols-2"
        >
          {(d.pending_requests?.package || 0) > 0 && (
            <Link
              to={`/t/${slug}/financeiro?tab=packages`}
              data-testid="dashboard-pending-package-link"
              className="rounded-xl border-2 border-amber-500/40 bg-amber-500/5 p-4 hover:border-amber-500/70 transition"
            >
              <div className="text-xs text-amber-700 dark:text-amber-200 font-semibold uppercase tracking-wider">
                Pacotes
              </div>
              <div className="font-display font-semibold text-sm mt-1">
                {d.pending_requests.package} pedido(s) de pacote aguardando aprovação
              </div>
              <div className="text-[11px] text-muted-foreground mt-1">
                Toque pra abrir a fila em Serviços → Pacotes.
              </div>
            </Link>
          )}
          {(d.pending_requests?.redemption || 0) > 0 && (
            <Link
              to={`/t/${slug}/fidelidade?tab=redeem`}
              data-testid="dashboard-pending-redemption-link"
              className="rounded-xl border-2 border-amber-500/40 bg-amber-500/5 p-4 hover:border-amber-500/70 transition"
            >
              <div className="text-xs text-amber-700 dark:text-amber-200 font-semibold uppercase tracking-wider">
                Fidelidade
              </div>
              <div className="font-display font-semibold text-sm mt-1">
                {d.pending_requests.redemption} resgate(s) aguardando entrega
              </div>
              <div className="text-[11px] text-muted-foreground mt-1">
                Toque pra abrir a fila em Fidelidade → Resgate.
              </div>
            </Link>
          )}
        </section>
      )}

      {/* Sentinela de Uso — pacotes E assinaturas com benefício ocioso */}
      {!isEstudio && (d.usage_sentinela || d.package_sentinela || []).length > 0 && (
        <UsageSentinelaCard slug={slug} items={d.usage_sentinela || d.package_sentinela || []} />
      )}

      {/* 8 widgets — KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Tile icon={Calendar} label="Agendamentos hoje" value={appts_today_n}
              sub={`${(d.appointments_today || []).filter(a => a.status === "scheduled").length} ainda em aberto`} />
        <Tile icon={DollarSign} label="Vendas do mês"
              value={`R$ ${revenue.toLocaleString("pt-BR", { minimumFractionDigits: 2 })}`}
              sub={`${d.sales_month?.appointments_completed || 0} atendimentos concluídos`} />
        {!isEstudio ? (
          <Tile icon={AlertTriangle} tone="text-red-600" label="Pendentes de retorno" value={inactive_total}
                sub={`${d.recurrence_counts?.lost_90 || 0} perdidos · ${d.recurrence_counts?.critical_60 || 0} críticos`} />
        ) : (
          <Tile icon={Calendar} label="Sessões concluídas"
                value={d.sales_month?.appointments_completed || 0}
                sub="no mês atual" />
        )}
        <Tile icon={Star} label="Nota de Satisfação" value={d.nps?.responses ? d.nps.score : "—"}
              sub={`${d.nps?.responses || 0} resp. nos últimos 30 dias`} />
      </div>

      {/* Pontos que requerem atenção — Central de Sugestões proativas */}
      <AttentionCard />

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Coluna 1: Agendamentos do dia */}
        <Section
          title="Agendamentos de hoje"
          subtitle="Confirmações e atendimentos do dia"
          action={<Link to={`/t/${slug}/agenda`} className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1">Ver agenda <ArrowRight className="w-3 h-3" /></Link>}
          testid="section-appts-today"
        >
          <Card className="rounded-tenant border-border">
            {(d.appointments_today || []).length === 0 ? (
              <div className="p-6 text-sm text-muted-foreground text-center">Sem agendamentos hoje. 🌿</div>
            ) : (
              <ul className="divide-y divide-border">
                {d.appointments_today.map((a) => (
                  <li key={a.id} className="px-4 py-2.5 flex items-center gap-3">
                    <div className="text-sm font-mono font-semibold w-12">{new Date(a.start_at).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}</div>
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium truncate">{a.client_name}</div>
                      <div className="text-xs text-muted-foreground truncate">{a.service_name}{a.resource_name ? ` · ${a.resource_name}` : ""}</div>
                    </div>
                    <Badge variant="outline" className={STATUS_TONE[a.status]}>{STATUS_LABEL[a.status]}</Badge>
                  </li>
                ))}
              </ul>
            )}
          </Card>
        </Section>

        {/* Coluna 2: Aniversariantes + Top serviços */}
        <div className="space-y-6">
          <Section title="Aniversariantes do mês" subtitle="Aproveite para presentear" testid="section-birthdays">
            <Card className="rounded-tenant border-border">
              {(d.birthdays_month || []).length === 0 ? (
                <div className="p-6 text-sm text-muted-foreground text-center">Sem aniversários este mês.</div>
              ) : (
                <ul className="divide-y divide-border">
                  {d.birthdays_month.slice(0, 6).map((b) => (
                    <li key={b.id} className="px-4 py-2.5 flex items-center justify-between gap-3">
                      <div className="flex items-center gap-2 min-w-0">
                        <Cake className="w-3.5 h-3.5 text-tenant shrink-0" />
                        <span className="text-sm truncate">{b.name}</span>
                      </div>
                      <Badge variant="outline">dia {String(b.day).padStart(2, "0")}</Badge>
                    </li>
                  ))}
                </ul>
              )}
            </Card>
          </Section>

          <Section title="Serviços mais procurados" subtitle="Últimos 30 dias" testid="section-top-services">
            <Card className="rounded-tenant border-border p-3">
              {(d.top_services || []).length === 0 ? (
                <div className="p-3 text-sm text-muted-foreground text-center">Sem atendimentos concluídos ainda.</div>
              ) : (
                <ul className="space-y-2">
                  {d.top_services.map((s, i) => {
                    const max = d.top_services[0]?.count || 1;
                    const pct = (s.count / max) * 100;
                    return (
                      <li key={s.service_id || i} className="space-y-1">
                        <div className="flex items-center justify-between gap-2 text-xs">
                          <span className="font-medium truncate flex items-center gap-1.5"><Scissors className="w-3 h-3" />{s.name}</span>
                          <span className="text-muted-foreground">{s.count}</span>
                        </div>
                        <div className="h-1.5 rounded-full bg-muted overflow-hidden">
                          <div className="h-full rounded-full" style={{ width: `${pct}%`, background: `hsl(var(--tenant-primary))` }} />
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </Card>
          </Section>

          {(d.top_resources || []).length > 0 && (
            <Section title={`Top ${term("resource_plural", { lower: true })}`} subtitle="Volume de atendimentos (30 dias)" testid="section-top-resources">
              <Card className="rounded-tenant border-border p-3">
                <ul className="space-y-2">
                  {d.top_resources.map((r, i) => {
                    const max = d.top_resources[0]?.count || 1;
                    const pct = (r.count / max) * 100;
                    return (
                      <li key={r.resource_id || i} className="space-y-1">
                        <div className="flex items-center justify-between gap-2 text-xs">
                          <span className="font-medium truncate">{r.name}</span>
                          <span className="text-muted-foreground">{r.count} · R$ {r.revenue.toFixed(0)}</span>
                        </div>
                        <div className="h-1.5 rounded-full bg-muted overflow-hidden">
                          <div className="h-full rounded-full" style={{ width: `${pct}%`, background: `hsl(var(--tenant-primary))` }} />
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </Card>
            </Section>
          )}
        </div>

        {/* Coluna 3: Oportunidades + Recorrência summary */}
        <div className="space-y-6">
          <Section title="Oportunidades de fidelização" subtitle="Aja antes da concorrência" testid="section-opportunities">
            <Card className="rounded-tenant border-border p-4 space-y-3">
              <OpportunityRow
                icon={MessageCircle}
                label="Podem indicar amigos"
                value={d.opportunities?.pode_indicar || 0}
                hint="100+ pontos · sem indicação ainda"
                to={`/t/${slug}/recorrencia`}
              />
              <OpportunityRow
                icon={Crown}
                label="Merecem um benefício"
                value={d.opportunities?.merece_beneficio || 0}
                hint="500+ pontos acumulados"
                to={`/t/${slug}/recorrencia`}
              />
              <OpportunityRow
                icon={Gift}
                label="Retornos esta semana"
                value={d.recurrence_counts?.next_7d || 0}
                hint="Próximos 7 dias por prazo de serviço"
                to={`/t/${slug}/recorrencia`}
              />
            </Card>
          </Section>

          <Section title="Clientes inativos" subtitle="Janelas de inatividade" testid="section-inactive">
            <Card className="rounded-tenant border-border p-4 space-y-2.5">
              <InactiveRow label="Atrasados 30 dias" value={d.recurrence_counts?.warning_30 || 0} tone="bg-amber-500/10 text-amber-700 border-amber-500/30" />
              <InactiveRow label="Críticos 60 dias" value={d.recurrence_counts?.critical_60 || 0} tone="bg-orange-500/10 text-orange-700 border-orange-500/30" />
              <InactiveRow label="Perdidos 90 dias+" value={d.recurrence_counts?.lost_90 || 0} tone="bg-red-500/10 text-red-700 border-red-500/30" />
              <Link to={`/t/${slug}/recorrencia`} className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1 pt-1">
                Abrir Central de Recorrência <ArrowRight className="w-3 h-3" />
              </Link>
            </Card>
          </Section>
        </div>
      </div>

      {/* Ações rápidas */}
      <div>
        <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground mb-3">Ações rápidas</div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <QuickLink to={`/t/${slug}/clientes`} icon={Users} label={`Novo ${term("client_singular", { lower: true })}`} sub="Cadastre e segmente" />
          <QuickLink to={`/t/${slug}/agenda`} icon={Calendar} label={`Agendar ${term("appointment_singular", { lower: true })}`} sub="Crie um novo horário" />
          <QuickLink to={`/t/${slug}/fidelidade`} icon={Sparkles} label="Configurar pontos" sub="Crie regras de pontuação" />
          <QuickLink to={`/t/${slug}/automacoes`} icon={TrendingUp} label="Criar automação" sub="Quando isso → Faça aquilo" />
        </div>
      </div>
    </div>
  );
}

function OpportunityRow({ icon: Icon, label, value, hint, to }) {
  return (
    <Link to={to} className="flex items-center justify-between gap-3 hover:bg-muted/40 rounded-lg p-2 -m-2 transition-colors group">
      <div className="flex items-center gap-3 min-w-0">
        <div className="w-9 h-9 rounded-lg grid place-items-center" style={{ background: `hsl(var(--tenant-primary) / 0.12)`, color: `hsl(var(--tenant-primary))` }}>
          <Icon className="w-4 h-4" strokeWidth={1.6} />
        </div>
        <div className="min-w-0">
          <div className="text-sm font-medium">{label}</div>
          <div className="text-[11px] text-muted-foreground">{hint}</div>
        </div>
      </div>
      <div className="text-2xl font-display font-bold" style={{ color: `hsl(var(--tenant-primary))` }}>{value}</div>
    </Link>
  );
}

function InactiveRow({ label, value, tone }) {
  return (
    <div className="flex items-center justify-between gap-3 text-sm">
      <span>{label}</span>
      <Badge variant="outline" className={tone}>{value}</Badge>
    </div>
  );
}

function QuickLink({ to, icon: Icon, label, sub }) {
  return (
    <Link to={to} className="group">
      <Card className="p-5 rounded-tenant border-border hover:border-tenant transition-colors">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg grid place-items-center text-tenant-foreground" style={{ background: `hsl(var(--tenant-primary))` }}>
            <Icon className="w-5 h-5" strokeWidth={1.6} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium">{label}</div>
            <div className="text-xs text-muted-foreground">{sub}</div>
          </div>
          <ArrowRight className="w-4 h-4 text-muted-foreground group-hover:text-foreground transition-colors" />
        </div>
      </Card>
    </Link>
  );
}

const RISK_LABEL = { warning_30: "atrasado 30d", critical_60: "crítico 60d", lost_90: "perdido 90d+" };
const RISK_TONE = {
  warning_30: "bg-amber-500/10 text-amber-700 border-amber-500/30",
  critical_60: "bg-orange-500/10 text-orange-700 border-orange-500/30",
  lost_90: "bg-red-500/10 text-red-700 border-red-500/30",
};

function RecoverySection({ slug, value, atRiskCount, urgent }) {
  const fmt = value.toLocaleString("pt-BR", { minimumFractionDigits: 2 });
  const anyUrgent = urgent && urgent.length > 0;

  const handleContact = (c) => {
    const first = (c.client_name || "").split(" ")[0] || "";
    const svc = c.last_service_name ? ` no seu ${c.last_service_name}` : "";
    const text =
      `Oi ${first}! Aqui é do salão 💜 Faz ${c.days_since_last} dias que você não vem${svc}. ` +
      `Que tal escolher um horário essa semana? Posso te encaixar rapidinho.`;
    sendWhatsApp({
      phone: c.phone,
      text,
      clientId: c.client_id,
      context: "dashboard_recovery",
    });
  };

  return (
    <section data-testid="dashboard-recovery-panel"
             className="grid grid-cols-1 lg:grid-cols-3 gap-4 lg:gap-5">
      {/* Card Oportunidade de Receita */}
      <Card
        className="p-6 rounded-tenant border-0 text-white shadow-lg lg:col-span-1"
        style={{
          background: "linear-gradient(135deg, hsl(var(--tenant-primary)) 0%, hsl(var(--tenant-primary) / 0.75) 100%)",
        }}
        data-testid="recoverable-value-card"
      >
        <div className="flex items-start justify-between">
          <div className="text-[10px] uppercase tracking-[0.22em] opacity-90">Oportunidade de Receita</div>
          <Wallet className="w-4 h-4 opacity-90" strokeWidth={1.5} />
        </div>
        <div className="mt-4 font-display font-bold tracking-tight text-4xl sm:text-5xl leading-none"
             data-testid="recoverable-value-amount">
          R$ {fmt}
        </div>
        <div className="mt-3 text-xs sm:text-sm opacity-90 leading-relaxed">
          Valor <b>recuperável este mês</b> se você reativar os{" "}
          <b>{atRiskCount} cliente{atRiskCount !== 1 ? "s" : ""}</b>{" "}
          em recorrência atrasada.
        </div>
        <Link
          to={`/t/${slug}/recorrencia`}
          className="mt-5 inline-flex items-center gap-1.5 text-xs font-medium bg-white/15 hover:bg-white/25 transition-colors px-3 py-1.5 rounded-md"
          data-testid="recoverable-open-risk-btn"
        >
          Abrir Gestor de Risco <ArrowRight className="w-3 h-3" />
        </Link>
      </Card>

      {/* Ações rápidas — Top 3 clientes urgentes com botão WhatsApp */}
      <Card className="p-4 sm:p-5 rounded-tenant border-border lg:col-span-2" data-testid="recoverable-urgent-list">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Ações rápidas</div>
            <div className="font-display text-base font-semibold flex items-center gap-1.5 mt-0.5">
              <Target className="w-4 h-4 text-tenant" strokeWidth={1.6} />
              Contate agora
            </div>
          </div>
          {anyUrgent && (
            <Link to={`/t/${slug}/recorrencia`}
                  className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1">
              Ver todos <ArrowRight className="w-3 h-3" />
            </Link>
          )}
        </div>
        {!anyUrgent ? (
          <div className="text-sm text-muted-foreground text-center py-8">
            🎉 Nenhum cliente urgente pra recuperar agora — ótimo trabalho!
          </div>
        ) : (
          <ul className="space-y-2">
            {urgent.map((c) => (
              <li
                key={c.client_id}
                className="flex flex-col sm:flex-row sm:items-center gap-3 p-3 rounded-lg border border-border bg-muted/30"
                data-testid={`urgent-client-${c.client_id}`}
              >
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-medium truncate">{c.client_name}</span>
                    <Badge variant="outline" className={`${RISK_TONE[c.risk_level] || ""} text-[10px]`}>
                      {RISK_LABEL[c.risk_level] || c.risk_level}
                    </Badge>
                  </div>
                  <div className="text-[11px] text-muted-foreground mt-0.5 truncate">
                    {c.days_since_last} dias sem vir
                    {c.last_service_name ? ` · último: ${c.last_service_name}` : ""}
                    {c.expected_value > 0 ? ` · ~R$ ${c.expected_value.toLocaleString("pt-BR", { minimumFractionDigits: 2 })}` : ""}
                  </div>
                </div>
                <Button
                  size="sm"
                  onClick={() => handleContact(c)}
                  data-testid={`urgent-client-wa-${c.client_id}`}
                  className="gap-1.5 w-full sm:w-auto bg-emerald-600 hover:bg-emerald-700 text-white shrink-0 h-10 sm:h-9"
                >
                  <MessageCircle className="w-4 h-4" strokeWidth={2} />
                  WhatsApp
                </Button>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </section>
  );
}

function UsageSentinelaCard({ slug, items }) {
  const handleContact = (it) => {
    const first = (it.client_name || "").split(" ")[0];
    const kind = it.type === "subscription" ? "assinatura" : "pacote";
    const text =
      `Oi ${first}! Passando aqui pra lembrar da sua ${kind} do ${it.benefit_name}. ` +
      `Já faz ${it.days_idle} dias — que tal marcar a próxima essa semana? Posso te encaixar num horário rápido.`;
    sendWhatsApp({
      phone: it.phone,
      text,
      clientId: it.client_id,
      context: `${it.type}_sentinela`,
    });
  };
  const iconFor = (t) => (t === "subscription" ? "🔁" : "📦");
  const labelFor = (t) => (t === "subscription" ? "Assinatura" : "Pacote");

  return (
    <section data-testid="package-sentinela">
      <Card className="rounded-tenant border-amber-500/30 p-4 sm:p-5"
            style={{ background: "hsl(var(--tenant-primary) / 0.04)" }}>
        <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-[0.2em] text-amber-700 flex items-center gap-1.5">
              <AlertTriangle className="w-3.5 h-3.5" /> Sentinela de Uso
            </div>
            <div className="font-display text-lg font-semibold mt-0.5">
              {items.length} benefício{items.length !== 1 ? "s" : ""} parado{items.length !== 1 ? "s" : ""}
            </div>
            <div className="text-xs text-muted-foreground">
              Clientes com pacote ou assinatura ativa mas sem agendar há mais que o dobro do intervalo sugerido.
            </div>
          </div>
          <Link to={`/t/${slug}/financeiro?tab=packages`}
                className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1">
            Ver todos <ArrowRight className="w-3 h-3" />
          </Link>
        </div>
        <ul className="space-y-2">
          {items.slice(0, 5).map((it) => {
            const key = it.ref_id || it.client_package_id;
            return (
              <li
                key={key}
                className="flex flex-col sm:flex-row sm:items-center gap-3 p-3 rounded-lg border border-border bg-background"
                data-testid={`sentinela-row-${key}`}
              >
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium truncate flex items-center gap-1.5">
                    <span>{iconFor(it.type)}</span>{it.client_name}
                    <Badge variant="outline" className="text-[10px]">{labelFor(it.type || "package")}</Badge>
                  </div>
                  <div className="text-[11px] text-muted-foreground mt-0.5 truncate">
                    {it.benefit_name || it.package_name} ·{" "}
                    {it.detail || `${it.sessions_remaining} restante(s)`} ·
                    {" "}{it.days_idle} dias parado (intervalo sugerido: {it.suggested_interval_days}d)
                  </div>
                </div>
                {it.phone ? (
                  <Button
                    size="sm"
                    onClick={() => handleContact(it)}
                    data-testid={`sentinela-wa-${key}`}
                    className="gap-1.5 w-full sm:w-auto bg-emerald-600 hover:bg-emerald-700 text-white shrink-0 h-10 sm:h-9"
                  >
                    <MessageCircle className="w-4 h-4" strokeWidth={2} /> WhatsApp
                  </Button>
                ) : (
                  <Badge variant="outline" className="text-[10px] shrink-0">Sem telefone</Badge>
                )}
              </li>
            );
          })}
        </ul>
      </Card>
    </section>
  );
}

function OnboardingChecklist({ slug, onboarding }) {
  const steps = [
    {
      key: "services",
      done: onboarding.has_services,
      label: "Cadastrar primeiro serviço",
      hint: "Adicione o primeiro serviço que você oferece",
      to: `/t/${slug}/financeiro?tab=services`,
    },
    {
      key: "loyalty",
      done: onboarding.has_loyalty,
      label: "Configurar fidelidade",
      hint: "Crie regras de pontos para gerar recompra",
      to: `/t/${slug}/fidelidade`,
    },
    {
      key: "appointment",
      done: onboarding.has_first_appointment,
      label: "Realizar primeiro atendimento",
      hint: "Feche o primeiro atendimento e comece a operar",
      to: `/t/${slug}/agenda`,
    },
  ];
  const doneCount = steps.filter((s) => s.done).length;
  const pct = Math.round((doneCount * 100) / steps.length);

  return (
    <Card
      className="p-5 sm:p-6 rounded-tenant border-tenant/40"
      style={{ background: "hsl(var(--tenant-primary) / 0.05)" }}
      data-testid="onboarding-checklist"
    >
      <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-[0.2em] text-tenant flex items-center gap-1.5">
            <Sparkles className="w-3.5 h-3.5" /> Primeiros passos
          </div>
          <div className="font-display text-lg sm:text-xl font-semibold mt-1">
            {doneCount === steps.length
              ? "Tudo pronto! Já pode explorar tudo 🚀"
              : `${doneCount} de ${steps.length} passos concluídos`}
          </div>
        </div>
        <div className="text-xs text-muted-foreground">
          Você está aqui há {onboarding.age_days} dia{onboarding.age_days !== 1 ? "s" : ""}
        </div>
      </div>
      <div className="h-1.5 rounded-full bg-muted mb-4 overflow-hidden">
        <div className="h-full rounded-full transition-all"
             style={{ width: `${pct}%`, background: "hsl(var(--tenant-primary))" }} />
      </div>
      <ul className="space-y-2" data-testid="onboarding-steps">
        {steps.map((s) => (
          <li key={s.key} data-testid={`onboarding-step-${s.key}`}>
            <Link
              to={s.to}
              className={`flex items-center gap-3 p-3 rounded-lg border transition-colors ${
                s.done
                  ? "border-emerald-500/30 bg-emerald-500/5 text-muted-foreground line-through"
                  : "border-border bg-background hover:border-tenant hover:bg-muted/30"
              }`}
            >
              {s.done ? (
                <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0" />
              ) : (
                <Circle className="w-5 h-5 text-muted-foreground shrink-0" />
              )}
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium">{s.label}</div>
                {!s.done && <div className="text-xs text-muted-foreground">{s.hint}</div>}
              </div>
              {!s.done && <ArrowRight className="w-4 h-4 text-muted-foreground shrink-0" />}
            </Link>
          </li>
        ))}
      </ul>
    </Card>
  );
}
